import {
  Button,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  theme,
  Tooltip,
} from 'antd'
import { useTranslation } from 'react-i18next'
import { formGutter } from '../../lib/layout'
import _filter from 'lodash/filter'
import _map from 'lodash/map'
import _sortBy from 'lodash/sortBy'
import { QuestionCircleTwoTone } from '@ant-design/icons'
import dayjs from 'dayjs'

export default function CompanyOperationsForm({
  configs,
  data,
  goBack,
  isFinalStep,
  loading,
  onCancel,
  requireNumberAndPin,
  sendFormValues,
  showU1Levy,
  showEstimator,
}) {
  const { t } = useTranslation()

  function handleSubmit(values) {
    const accounting = {
      registryNumber: values.accounting.registryNumber.trim(),
      immediateReportingObligation:
        values.accounting.immediateReportingObligation,
    }

    if (showU1Levy) {
      accounting.u1Levy =
        values.accounting.u1LevyEnabled === false
          ? 'none'
          : (values.accounting.u1Levy ?? null)
    }

    if (showEstimator) {
      accounting.estimator = values.accounting.estimator
    }

    const finalValues = {
      accounting: accounting,
      tradeAssociation: {
        name: values.tradeAssociation.name,
        number: values.tradeAssociation.number?.trim() ?? null,
        pin: values.tradeAssociation.pin?.trim() ?? null,
      },
    }

    sendFormValues(finalValues, isFinalStep, data?.uid)
  }

  const [form] = Form.useForm()

  const u1LevyEnabled = Form.useWatch(['accounting', 'u1LevyEnabled'], {
    form,
    preserve: true,
  })

  const initialValues = {
    ...data,
    accounting: {
      ...data.accounting,
      u1LevyEnabled: !data?.accounting?.u1Levy
        ? null
        : data?.accounting?.u1Levy !== 'none',
      u1Levy:
        data?.accounting?.u1Levy === 'none' ? null : data?.accounting?.u1Levy,
    },
  }

  const { token } = theme.useToken()

  return (
    <Form
      layout="vertical"
      onFinish={handleSubmit}
      form={form}
      initialValues={initialValues}
    >
      <Row gutter={formGutter}>
        <Col xs={24} md={12}>
          <RegistryNumberFormItem configs={configs} />
        </Col>
        <Col xs={24} md={12}>
          <TradeAssociationFormItem
            configs={configs}
            storedTradeAssociation={data?.tradeAssociation?.name}
          />
        </Col>
        <Col xs={24} md={12}>
          <TradeAssociationNumberFormItem required={requireNumberAndPin} />
        </Col>
        <Col xs={24} md={12}>
          <TradeAssociationPinFormItem required={requireNumberAndPin} />
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={
              <Space>
                {t('frontend:company.immediateReportingObligation')}
                <Tooltip
                  title={t('frontend:company.immediateReportingTooltip')}
                >
                  <QuestionCircleTwoTone />
                </Tooltip>
              </Space>
            }
            name={['accounting', 'immediateReportingObligation']}
            rules={[
              {
                required: true,
                message: t('frontend:company.required'),
              },
            ]}
          >
            <Radio.Group buttonStyle="solid">
              <Radio.Button id="immediateReportingObligationYes" value={true}>
                {t('frontend:common.yes')}
              </Radio.Button>
              <Radio.Button id="immediateReportingObligationNo" value={false}>
                {t('frontend:common.no')}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>
        {showEstimator && (
          <Col xs={24} md={12}>
            <Form.Item
              label={t('frontend:company.estimator')}
              name={['accounting', 'estimator']}
              rules={[
                {
                  required: true,
                  message: t('frontend:company.required'),
                },
              ]}
            >
              <Radio.Group buttonStyle="solid">
                <Radio.Button id="estimatorYes" value={true}>
                  {t('frontend:common.yes')}
                </Radio.Button>
                <Radio.Button id="estimatorNo" value={false}>
                  {t('frontend:common.no')}
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        )}
        {showU1Levy && (
          <>
            <Col xs={24} md={12}>
              <Form.Item
                name={['accounting', 'u1LevyEnabled']}
                label={t('frontend:company.u1LevyPaid')}
                rules={[
                  {
                    required: true,
                    message: t('frontend:company.required'),
                  },
                ]}
              >
                <Radio.Group buttonStyle="solid">
                  <Radio.Button value={true}>
                    {t('frontend:common.yes')}
                  </Radio.Button>
                  <Radio.Button value={false}>
                    {t('frontend:common.no')}
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            {u1LevyEnabled && (
              <Col xs={24} md={12}>
                <Form.Item
                  name={['accounting', 'u1Levy']}
                  label={t('frontend:company.u1LevyRate')}
                  rules={[
                    {
                      required: true,
                      message: t('frontend:company.required'),
                    },
                  ]}
                >
                  <Radio.Group
                    buttonStyle="solid"
                    style={{
                      marginTop: token.marginXS,
                      marginLeft: token.marginXS,
                    }}
                  >
                    <Space direction="vertical">
                      <Radio value="standard">
                        {t('frontend:company.u1LevyStandard')}
                      </Radio>
                      <Radio value="reduced">
                        {t('frontend:company.u1LevyReduced')}
                      </Radio>
                      <Radio value="increased">
                        {t('frontend:company.u1LevyIncreased')}
                      </Radio>
                      <Radio value="mixed">
                        {t('frontend:company.u1LevyMixed')}
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Col>
            )}
          </>
        )}
        <Col xs={24} style={{ textAlign: 'right' }}>
          <Space>
            {onCancel && (
              <Button onClick={onCancel}>{t('frontend:common.cancel')}</Button>
            )}
            {goBack && (
              <Button type="text" onClick={() => goBack(null)}>
                {t('frontend:common.back')}
              </Button>
            )}
            <Button type="primary" htmlType="submit" loading={loading}>
              {isFinalStep
                ? t('frontend:company.save')
                : t('frontend:company.next')}
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  )
}

function selectableTradeAssociations(configs, storedTradeAssociation) {
  const today = dayjs().format('YYYY-MM-DD')
  return _sortBy(
    _filter(
      configs?.tradeAssociations,
      (c) =>
        c.uid === storedTradeAssociation ||
        !c.validUntil ||
        c.validUntil >= today
    ),
    'name'
  )
}

export function RegistryNumberFormItem({ configs }) {
  const { t } = useTranslation()
  const tradeAssociationNumbers = _map(configs?.tradeAssociations, 'number')

  return (
    <Form.Item
      label={t('frontend:company.registryNumber')}
      name={['accounting', 'registryNumber']}
      rules={[
        {
          required: true,
          whitespace: true,
          message: t('frontend:company.required'),
        },
        { len: 8, message: t('frontend:company.registryNumberLength') },
        {
          pattern: new RegExp(/^[0-9]*$/i),
          message: t('frontend:company.digitsOnly'),
        },
        {
          validator: (_, value) =>
            tradeAssociationNumbers.indexOf(value) === -1
              ? Promise.resolve()
              : Promise.reject(
                  new Error(
                    t('frontend:company.registryNumberOwnNotAssociation')
                  )
                ),
        },
      ]}
    >
      <Input />
    </Form.Item>
  )
}

export function TradeAssociationFormItem({ configs, storedTradeAssociation }) {
  const { t } = useTranslation()
  const tradeAssociations = selectableTradeAssociations(
    configs,
    storedTradeAssociation
  )

  return (
    <Form.Item
      label={t('frontend:company.tradeAssociation')}
      name={['tradeAssociation', 'name']}
      rules={[
        {
          required: true,
          whitespace: true,
          message: t('frontend:company.required'),
        },
      ]}
    >
      <Select
        showSearch
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {_map(tradeAssociations, (c) => (
          <Select.Option key={c.uid} value={c.uid}>
            {c.name}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  )
}

export function TradeAssociationNumberFormItem({ required }) {
  const { t } = useTranslation()

  return (
    <Form.Item
      label={t('frontend:company.companyNumberAssociation')}
      name={['tradeAssociation', 'number']}
      rules={[
        {
          len: 15,
          message: t('frontend:company.companyNumberLength'),
        },
        {
          pattern: new RegExp(/^[0-9]*$/i),
          message: t('frontend:company.digitsOnly'),
        },
        {
          required,
          message: t('frontend:company.required'),
        },
      ]}
    >
      <Input />
    </Form.Item>
  )
}

export function TradeAssociationPinFormItem({ required }) {
  const { t } = useTranslation()

  return (
    <Form.Item
      label={t('frontend:company.pinAssociation')}
      name={['tradeAssociation', 'pin']}
      rules={[
        { max: 6, message: t('frontend:company.pinMaxLength') },
        {
          pattern: new RegExp(/^[0-9]*$/i),
          message: t('frontend:company.digitsOnly'),
        },
        {
          required,
          message: t('frontend:company.required'),
        },
      ]}
    >
      <Input />
    </Form.Item>
  )
}
